import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { 
  Bot, ArrowRight, ArrowLeft, Check, Upload, FileText, 
  Globe, Palette, Rocket, Sparkles, Database,
  MessageSquare, Loader2, X, CheckCircle,
  Zap, Target, Users, Calendar, Share2,
  Send, Facebook, Instagram, Phone,
  Headphones, HelpCircle, Heart, Star
} from 'lucide-react';
import { toast } from 'sonner';
import { chatbotsAPI, knowledgeAPI, channelsAPI } from '@/services/api';
import { IconSelector } from '@/components/common/IconSelector';
import { cn } from '@/lib/utils';
import { PERSONAS } from '@/data/personas';

const STEPS = [
  { id: 'basics',      title: 'Basic Info',  icon: Bot },
  { id: 'knowledge',   title: 'Knowledge',   icon: Database },
  { id: 'personality', title: 'Personality', icon: Sparkles },
  { id: 'channels',    title: 'Channels',    icon: Share2 },
  { id: 'theme',       title: 'Appearance',  icon: Palette },
  { id: 'deploy',      title: 'Deploy',      icon: Rocket },
];

const CHANNEL_DEFS = [
  {
    id: 'website',
    name: 'Website Widget',
    icon: Globe,
    color: 'blue',
    desc: 'Embed on any website — gets generated automatically.',
    always: true,
  },
  {
    id: 'whatsapp',
    name: 'WhatsApp',
    icon: Phone,
    color: 'green',
    desc: 'Connect via WhatsApp Business API to auto-reply and capture leads.',
    fields: [
      { key: 'phone',       label: 'Phone Number ID',              placeholder: '1234567890 (from Meta Business)' },
      { key: 'business_id', label: 'WhatsApp Business Account ID', placeholder: 'WABA ID from Meta' },
      { key: 'access_token', label: 'Permanent Access Token',      placeholder: 'EAAxxxxxxx', type: 'password' },
    ],
  },
  {
    id: 'facebook',
    name: 'Facebook Messenger',
    icon: Facebook,
    color: 'indigo',
    desc: 'Reply to Messenger conversations automatically from your Facebook Page.',
    fields: [
      { key: 'page_id',      label: 'Facebook Page ID',  placeholder: '1234567890' },
      { key: 'access_token', label: 'Page Access Token', placeholder: 'EAAxxxxxxx', type: 'password' },
    ],
  },
  {
    id: 'instagram',
    name: 'Instagram DMs',
    icon: Instagram,
    color: 'pink',
    desc: 'Auto-respond to Instagram DMs linked to a Facebook Business Page.',
    fields: [
      { key: 'account_id',   label: 'Instagram Business Account ID', placeholder: 'From Meta Business Suite' },
      { key: 'access_token', label: 'Page Access Token (same as Facebook)', placeholder: 'EAAxxxxxxx', type: 'password' },
    ],
  },
  {
    id: 'telegram',
    name: 'Telegram',
    icon: Send,
    color: 'sky',
    desc: 'Add your Telegram Bot Token from @BotFather to go live immediately.',
    fields: [
      { key: 'bot_token', label: 'Telegram Bot Token', placeholder: '123456:ABCDef...', type: 'password' },
    ],
  },
];

const TEMPLATES = PERSONAS.map(p => ({
  id: p.id,
  name: p.displayName ? `${p.displayName} — ${p.name}` : p.name,
  icon: p.icon,
  desc: p.description,
  prompt: p.prompt,
}));
const CUSTOM_TEMPLATE_INDEX = TEMPLATES.findIndex(t => t.id === 'custom');

const CreateAgentWizard = ({ isOpen, onClose, accounts = [], subAccounts = {}, featureAccess = {} }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [existingNames, setExistingNames] = useState([]);

  // Guarded close — prompts the user if they have unsaved input
  const handleClose = () => {
    const hasInput = name.trim() || description.trim() || urls.length > 0 || files.length > 0 || rules.length > 0;
    if (hasInput && !submitting) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        return;
      }
    }
    onClose();
  };

  // Check if social channels are allowed by the plan
  const socialChannelsEnabled = featureAccess?.social_channels !== false;

  // Step 0: Basics
  const [accountId, setAccountId] = useState('');
  const [subAccountId, setSubAccountId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [welcome, setWelcome] = useState('Hello! How can I help you today?');
  const [template, setTemplate] = useState('custom');
  const [icon, setIcon] = useState('Bot');
  const [iconColor, setIconColor] = useState('blue');
  const [avatarUrl, setAvatarUrl] = useState('');

  // Step 1: Knowledge
  const [files, setFiles] = useState([]);
  const [urls, setUrls] = useState([]);
  const [newUrl, setNewUrl] = useState('');

  // Step 2: Personality
  const [prompt, setPrompt] = useState(TEMPLATES[CUSTOM_TEMPLATE_INDEX]?.prompt || '');
  const [rules, setRules] = useState([]);
  const [newRule, setNewRule] = useState('');
  const [tone, setTone] = useState('balanced');
  const [followup, setFollowup] = useState(true);

  // Step 3: Channels
  const [channelToggles, setChannelToggles] = useState({
    website: true, whatsapp: false, facebook: false, instagram: false, telegram: false,
  });
  const [channelCreds, setChannelCreds] = useState({
    whatsapp: {}, facebook: {}, instagram: {}, telegram: {},
  });

  // Step 4: Theme
  const [theme, setTheme] = useState({
    primary_color: '#3b82f6', secondary_color: '#f1f5f9',
    text_color: '#1f2937', background_color: '#ffffff',
    border_radius: 16, position: 'bottom-right', widget_style: 'floating',
    widget_button_icon: 'chat', widget_button_text: '',
  });

  // ---- Auto-select account & sub-account ----
  useEffect(() => {
    if (!isOpen) return;
    // Reset state when wizard opens
    setStep(0);
    setName(''); setDescription('');
    setWelcome('Hello! How can I help you today?');
    setTemplate('custom'); setIcon('Bot'); setIconColor('blue');
    setAvatarUrl('');
    setFiles([]); setUrls([]); setNewUrl('');
    setPrompt(TEMPLATES[CUSTOM_TEMPLATE_INDEX]?.prompt || ''); setRules([]); setNewRule('');
    setTone('balanced'); setFollowup(true);
    setChannelToggles({ website: true, whatsapp: false, facebook: false, instagram: false, telegram: false });
    setChannelCreds({ whatsapp: {}, facebook: {}, instagram: {}, telegram: {} });
    setSubmitting(false);
    setTheme({
      primary_color: '#3b82f6', secondary_color: '#f1f5f9',
      text_color: '#1f2937', background_color: '#ffffff',
      border_radius: 16, position: 'bottom-right', widget_style: 'floating',
      widget_button_icon: 'chat', widget_button_text: '',
    });
    // Load existing agent names (for duplicate check)
    chatbotsAPI.listAll().then((res) => {
      const list = res.data?.chatbots || res.data || [];
      const items = Array.isArray(list) ? list : (list.items || []);
      setExistingNames(items.map((c) => (c.name || '').trim().toLowerCase()).filter(Boolean));
    }).catch(() => setExistingNames([]));
  }, [isOpen]);

  // Robust account auto-selection: try on every render cycle until set
  useEffect(() => {
    if (accounts.length > 0 && !accountId) {
      setAccountId(accounts[0].account_id);
    }
  }, [accounts, accountId]);

  useEffect(() => {
    if (accountId) {
      const subs = subAccounts[accountId] || [];
      if (subs.length > 0 && !subAccountId) {
        setSubAccountId(subs[0].sub_account_id);
      }
    }
  }, [accountId, subAccounts, subAccountId]);

  // Sync prompt when template changes
  useEffect(() => {
    const t = TEMPLATES.find(t => t.id === template);
    if (t) setPrompt(t.prompt);
  }, [template]);

  const availableSubs = useMemo(() => 
    accountId ? (subAccounts[accountId] || []) : [], 
    [accountId, subAccounts]
  );

  const progress = ((step + 1) / STEPS.length) * 100;

  const canProceed = useCallback(() => {
    if (step === 0) {
      const trimmed = name.trim();
      const isDuplicate = trimmed && existingNames.includes(trimmed.toLowerCase());
      return trimmed.length > 0 && !isDuplicate && accountId && subAccountId;
    }
    return true;
  }, [step, name, accountId, subAccountId, existingNames]);

  const handleNext = () => { if (step < STEPS.length - 1 && canProceed()) setStep(s => s + 1); };
  const handleBack = () => { if (step > 0) setStep(s => s - 1); };

  const handleFileUpload = (e) => {
    // Client-side validation:
    //   • Extension whitelist (matches backend acceptance list)
    //   • 10 MB per-file cap (matches backend 413 limit)
    // Blocks the upload at selection time so users don't wait for a
    // backend round-trip only to see "File too large".
    const ACCEPTED = ['pdf', 'docx', 'txt', 'csv', 'md'];
    const MAX_BYTES = 10 * 1024 * 1024;
    const picked = Array.from(e.target.files || []);
    const accepted = [];
    for (const f of picked) {
      const ext = (f.name.split('.').pop() || '').toLowerCase();
      if (!ACCEPTED.includes(ext)) {
        toast.error(`"${f.name}" is not a supported file type. Allowed: ${ACCEPTED.map(x => '.' + x).join(', ')}`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`"${f.name}" exceeds the 10 MB limit (file is ${(f.size / 1024 / 1024).toFixed(1)} MB)`);
        continue;
      }
      accepted.push({ name: f.name, size: f.size, file: f });
    }
    if (accepted.length) {
      setFiles(prev => [...prev, ...accepted]);
    }
    e.target.value = '';
  };

  const handleAddUrl = () => {
    if (!newUrl.trim()) return;
    try { new URL(newUrl); setUrls(prev => [...prev, newUrl.trim()]); setNewUrl(''); }
    catch { toast.error('Enter a valid URL'); }
  };

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      const res = await chatbotsAPI.create(accountId, subAccountId, {
        name: name.trim(),
        description: description.trim(),
        welcome_message: welcome.trim(),
        system_prompt: prompt.trim(),
        response_tone: tone,
        enable_followup_questions: followup,
        icon, icon_color: iconColor,
        avatar_url: avatarUrl,
        widget_theme: theme,
        rules: rules.map((r, i) => ({
          rule_id: `rule_${Date.now()}_${i}`, content: r,
          is_active: true, priority: rules.length - i, created_at: new Date().toISOString()
        })),
        status: 'active'
      });
      const agent = res.data;

      // Upload files
      for (const f of files) {
        try { await knowledgeAPI.uploadDocument(agent.account_id, agent.sub_account_id, agent.chatbot_id, f.file); }
        catch (err) { console.error('Upload failed:', f.name, err); }
      }
      // Add URLs
      for (const url of urls) {
        try { await knowledgeAPI.addUrlSource(agent.account_id, agent.sub_account_id, agent.chatbot_id, { url, name: new URL(url).hostname }); }
        catch (err) { console.error('URL add failed:', url, err); }
      }

      // Save enabled channels
      for (const ch of ['whatsapp', 'facebook', 'instagram', 'telegram']) {
        if (channelToggles[ch]) {
          const creds = channelCreds[ch] || {};
          const hasAnyField = Object.values(creds).some(v => v && v.trim());
          if (hasAnyField) {
            try {
              await channelsAPI.saveChannel(agent.chatbot_id, ch, creds);
            } catch (err) {
              console.error(`Channel save failed for ${ch}:`, err);
            }
          }
        }
      }

      toast.success('AI Employee created successfully!');
      navigate(`/dashboard/chatbots/${agent.chatbot_id}`, { state: { chatbot: agent } });
      onClose();
    } catch (error) {
      console.error('Error creating agent:', error);
      toast.error(error.response?.data?.detail || 'Failed to create AI Employee');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Step Renderers ----
  const renderBasics = () => (
    <div className="space-y-6">
      <div className="text-center mb-4">
        <h3 className="text-xl font-semibold text-slate-900">Let's create your AI Employee</h3>
        <p className="text-sm text-slate-500 mt-1">Choose a template that matches your use case. This sets the initial behavior and prompt for your agent, so it's ready to help your customers right away.</p>
      </div>

      {/* Template Selection */}
      <div>
        <Label className="text-slate-700 mb-3 block text-sm">What will your AI Employee do?</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-96 overflow-y-auto pr-1">
          {TEMPLATES.map(t => (
            <Card 
              key={t.id}
              className={cn("cursor-pointer transition-all hover:border-teal-300",
                template === t.id ? "border-teal-500 bg-teal-50 ring-2 ring-teal-200" : "border-slate-200"
              )}
              onClick={() => setTemplate(t.id)}
              data-testid={`template-${t.id}`}
            >
              <CardContent className="p-4 flex items-start gap-3">
                <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0",
                  template === t.id ? "bg-teal-500 text-white" : "bg-slate-100 text-slate-600"
                )}>
                  <t.icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 text-sm">{t.name}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{t.desc}</p>
                </div>
                {template === t.id && <CheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0" />}
              </CardContent>
            </Card>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-2">Selecting a persona prefills the system prompt — you can edit it further in step 3 (Personality) for your business.</p>
      </div>

      {/* Name + Icon */}
      <div className="flex items-start gap-4">
        <div>
          <Label className="text-slate-700 mb-2 block text-sm">Icon</Label>
          <IconSelector value={icon} onChange={setIcon} color={iconColor} onColorChange={setIconColor} />
        </div>
        <div className="flex-1 min-w-0">
          <Label className="text-slate-700 mb-2 block text-sm">Agent Name *</Label>
          <Input
            value={name}
            onChange={e => setName(e.target.value.slice(0, 60))}
            placeholder="e.g., Sales Assistant, Support Bot"
            className="bg-white border-slate-300 truncate"
            data-testid="agent-name-input"
            maxLength={60}
          />
          {name && existingNames.includes(name.trim().toLowerCase()) && (
            <p className="text-xs text-red-600 mt-1" data-testid="agent-name-duplicate-error">
              An agent with this name already exists. Pick a different name.
            </p>
          )}
          <p className="text-xs text-slate-400 mt-1">{name.length}/60 characters</p>
        </div>
      </div>

      {/* Description */}
      <div>
        <Label className="text-slate-700 mb-2 block text-sm">Description (optional)</Label>
        <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="What does this agent do?" className="bg-white border-slate-300 resize-none" rows={2} />
      </div>

      {/* Welcome Message */}
      <div>
        <Label className="text-slate-700 mb-2 block text-sm">Welcome Message</Label>
        <Textarea value={welcome} onChange={e => setWelcome(e.target.value)} placeholder="First message visitors see" className="bg-white border-slate-300 resize-none" rows={2} />
      </div>

      {/* Avatar Upload (optional) */}
      <div>
        <Label className="text-slate-700 mb-2 block text-sm">AI Employee Avatar <span className="text-slate-400 text-xs">(optional)</span></Label>
        <div className="flex items-center gap-3">
          {avatarUrl ? (
            <img src={avatarUrl} alt="avatar" className="w-12 h-12 rounded-full object-cover border" data-testid="wizard-avatar-preview" />
          ) : (
            <div className="w-12 h-12 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-400">
              <Bot className="w-5 h-5" />
            </div>
          )}
          <Input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              if (f.size > 2 * 1024 * 1024) { toast.error('Avatar must be under 2MB'); return; }
              const reader = new FileReader();
              reader.onloadend = () => setAvatarUrl(reader.result);
              reader.readAsDataURL(f);
            }}
            className="bg-white border-slate-300"
            data-testid="wizard-avatar-upload"
          />
          {avatarUrl && (
            <Button type="button" size="sm" variant="ghost" onClick={() => setAvatarUrl('')} className="text-red-600" data-testid="wizard-avatar-remove">
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
        <p className="text-xs text-slate-400 mt-1">Square image recommended. Falls back to icon if no avatar uploaded.</p>
      </div>

      {/* Workspace selector (only if multiple accounts) */}
      {accounts.length > 1 && (
        <div>
          <Label className="text-slate-700 mb-2 block text-sm">Workspace</Label>
          <Select value={accountId} onValueChange={v => {
            setAccountId(v);
            setSubAccountId('');
            const subs = subAccounts[v] || [];
            if (subs.length > 0) setSubAccountId(subs[0].sub_account_id);
          }}>
            <SelectTrigger className="bg-white border-slate-300"><SelectValue placeholder="Select workspace" /></SelectTrigger>
            <SelectContent>
              {accounts.map(a => <SelectItem key={a.account_id} value={a.account_id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Department selector (only if multiple sub-accounts) */}
      {availableSubs.length > 1 && (
        <div>
          <Label className="text-slate-700 mb-2 block text-sm">Department</Label>
          <Select value={subAccountId} onValueChange={setSubAccountId}>
            <SelectTrigger className="bg-white border-slate-300"><SelectValue placeholder="Select department" /></SelectTrigger>
            <SelectContent>
              {availableSubs.map(s => <SelectItem key={s.sub_account_id} value={s.sub_account_id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Show selected context */}
      {accountId && subAccountId && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600">
          <span className="font-medium">Creating in:</span> {accounts.find(a => a.account_id === accountId)?.name} / {availableSubs.find(s => s.sub_account_id === subAccountId)?.name}
        </div>
      )}
    </div>
  );

  const renderKnowledge = () => (
    <div className="space-y-6">
      <div className="text-center mb-4">
        <h3 className="text-xl font-semibold text-slate-900">Train your AI Employee</h3>
        <p className="text-sm text-slate-500 mt-1">Upload your business documents (FAQs, pricing, policies, service info) or add website URLs. Your AI agent will use this knowledge to answer customer questions accurately and with your specific details.</p>
      </div>

      {/* File Upload */}
      <div>
        <Label className="text-slate-700 mb-3 block text-sm">Upload Documents</Label>
        <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-blue-300 transition-colors relative">
          <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
          <p className="text-sm text-slate-600 mb-1">Drop files here or click to browse</p>
          <p className="text-xs text-slate-400">PDF, DOCX, TXT, CSV (max 10MB each)</p>
          <input type="file" multiple accept=".pdf,.docx,.txt,.csv,.md" className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleFileUpload} data-testid="file-upload-input" />
        </div>
        {files.length > 0 && (
          <div className="mt-3 space-y-2">
            {files.map((f, i) => (
              <div key={`file-${i}-${f.name}-${f.size}`} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-slate-700">{f.name}</span>
                  <span className="text-xs text-slate-400">({(f.size / 1024).toFixed(1)} KB)</span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))}>
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* URLs */}
      <div>
        <Label className="text-slate-700 mb-3 block text-sm">Add Website URLs</Label>
        <div className="flex gap-2">
          <Input value={newUrl} onChange={e => setNewUrl(e.target.value)} placeholder="https://example.com" className="bg-white border-slate-300" onKeyDown={e => e.key === 'Enter' && handleAddUrl()} data-testid="url-input" />
          <Button onClick={handleAddUrl} variant="outline" className="border-slate-300"><Globe className="w-4 h-4 mr-1" /> Add</Button>
        </div>
        {urls.length > 0 && (
          <div className="mt-3 space-y-2">
            {urls.map((u, i) => (
              <div key={`url-${i}-${u}`} className="flex items-center justify-between p-2 bg-blue-50 rounded-lg border border-blue-100">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-blue-700 truncate max-w-md">{u}</span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setUrls(prev => prev.filter((_, j) => j !== i))}>
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
        <p>This step is optional. You can always add more knowledge later from the agent settings.</p>
      </div>
    </div>
  );

  const renderPersonality = () => (
    <div className="space-y-6">
      <div className="text-center mb-4">
        <h3 className="text-xl font-semibold text-slate-900">Define Personality & Rules</h3>
        <p className="text-sm text-slate-500 mt-1">Shape how your agent communicates. The system prompt tells the AI who it is and how to behave. Business rules set hard boundaries your agent will never cross, like "never give refunds over $100" or "always ask for an email first."</p>
      </div>

      <div>
        <Label className="text-slate-700 mb-2 block text-sm">System Prompt</Label>
        <Textarea value={prompt} onChange={e => setPrompt(e.target.value)} className="bg-white border-slate-300 resize-none font-mono text-sm" rows={4} data-testid="system-prompt" />
      </div>

      <div>
        <Label className="text-slate-700 mb-3 block text-sm">Response Tone</Label>
        <div className="grid grid-cols-3 gap-2">
          {[{ id: 'professional', label: 'Professional' }, { id: 'balanced', label: 'Balanced' }, { id: 'casual', label: 'Casual' }].map(t => (
            <Button key={t.id} variant={tone === t.id ? 'default' : 'outline'} className={tone === t.id ? 'bg-teal-600' : ''} onClick={() => setTone(t.id)} data-testid={`tone-${t.id}`}>
              {t.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
        <div>
          <p className="text-sm font-medium text-slate-900">Follow-up Questions</p>
          <p className="text-xs text-slate-500">AI suggests next questions to keep conversation flowing</p>
        </div>
        <Switch checked={followup} onCheckedChange={setFollowup} />
      </div>

      <div>
        <Label className="text-slate-700 mb-2 block text-sm">Business Rules</Label>
        <div className="flex gap-2">
          <Input value={newRule} onChange={e => setNewRule(e.target.value)} placeholder="e.g., Never discuss competitor products" className="bg-white border-slate-300" onKeyDown={e => e.key === 'Enter' && (rules.length < 10) && newRule.trim() && (setRules(p => [...p, newRule.trim()]), setNewRule(''))} />
          <Button onClick={() => { if (newRule.trim()) { setRules(p => [...p, newRule.trim()]); setNewRule(''); }}} variant="outline" disabled={!newRule.trim() || rules.length >= 10}>Add</Button>
        </div>
        {rules.length > 0 && (
          <div className="mt-3 space-y-2">
            {rules.map((r, i) => (
              <div key={`rule-${i}-${r.slice(0, 12)}`} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg border border-slate-200">
                <span className="text-sm text-slate-700">{r}</span>
                <Button variant="ghost" size="sm" onClick={() => setRules(p => p.filter((_, j) => j !== i))}><X className="w-3 h-3" /></Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderChannels = () => {
    // Filter channels based on feature access - social channels may be disabled by plan
    const availableChannels = CHANNEL_DEFS.filter(ch => {
      // Website widget is always available
      if (ch.id === 'website') return true;
      // Social channels (whatsapp, facebook, instagram, telegram) require social_channels access
      return socialChannelsEnabled;
    });

    return (
    <div className="space-y-5">
      <div className="text-center mb-2">
        <h3 className="text-xl font-semibold text-slate-900">Connect Social Channels</h3>
        <p className="text-sm text-slate-500 mt-1">Choose where your AI Employee will receive and respond to messages. Each channel converts inbound enquiries into verified, trackable leads.</p>
      </div>

      {!socialChannelsEnabled && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
          <p className="text-amber-800 text-sm font-medium">Social channels (WhatsApp, Facebook, Instagram, Telegram) are not available on your current plan.</p>
          <p className="text-amber-600 text-xs mt-1">Upgrade your plan to connect social messaging channels.</p>
        </div>
      )}

      <div className="space-y-3">
        {availableChannels.map(ch => {
          const enabled = channelToggles[ch.id];
          const Icon = ch.icon;
          const colorMap = {
            blue: 'bg-blue-100 text-blue-600',
            green: 'bg-green-100 text-green-600',
            indigo: 'bg-indigo-100 text-indigo-600',
            pink: 'bg-pink-100 text-pink-600',
            sky: 'bg-sky-100 text-sky-600',
          };

          return (
            <div key={ch.id} className={cn(
              'rounded-xl border transition-all overflow-hidden',
              enabled ? 'border-teal-400 bg-teal-50/50' : 'border-slate-200 bg-white'
            )}>
              {/* Channel header row */}
              <div className="flex items-center gap-3 p-4">
                <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0', colorMap[ch.color])}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-slate-900 text-sm">{ch.name}</p>
                    {ch.always && <Badge className="text-xs bg-blue-100 text-blue-700 border-blue-200">Always On</Badge>}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{ch.desc}</p>
                </div>
                {!ch.always && (
                  <Switch
                    checked={enabled}
                    onCheckedChange={v => setChannelToggles(p => ({ ...p, [ch.id]: v }))}
                    data-testid={`channel-toggle-${ch.id}`}
                  />
                )}
              </div>

              {/* Credential fields (only for enabled non-always channels) */}
              {enabled && ch.fields && (
                <div className="px-4 pb-4 space-y-3 border-t border-teal-100 pt-3">
                  {ch.fields.map(f => (
                    <div key={f.key}>
                      <Label className="text-xs text-slate-600 mb-1 block">{f.label}</Label>
                      <Input
                        type={f.type || 'text'}
                        placeholder={f.placeholder}
                        value={channelCreds[ch.id]?.[f.key] || ''}
                        onChange={e => setChannelCreds(p => ({ ...p, [ch.id]: { ...p[ch.id], [f.key]: e.target.value } }))}
                        className="bg-white border-slate-300 text-sm h-9"
                        data-testid={`channel-field-${ch.id}-${f.key}`}
                      />
                    </div>
                  ))}
                  <p className="text-xs text-amber-700 bg-amber-50 rounded px-3 py-2 border border-amber-100">
                    Credentials are saved securely. You can update them anytime from the agent settings.
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600">
        All channels are optional. You can connect or disconnect them anytime from the <strong>Channels</strong> tab in your agent settings.
      </div>
    </div>
  );
  };

  const renderTheme = () => (
    <div className="space-y-6">
      <div className="text-center mb-4">
        <h3 className="text-xl font-semibold text-slate-900">Customize Appearance</h3>
        <p className="text-sm text-slate-500 mt-1">Make the chat widget match your brand. Pick colors that align with your website so the AI agent feels like a natural part of your business, not a third-party add-on.</p>
      </div>

      {/* Widget Style */}
      <div>
        <Label className="text-slate-700 mb-3 block text-sm">Widget Style</Label>
        <div className="grid grid-cols-2 gap-3">
          {[
            { id: 'floating', label: 'Floating Button', desc: 'Circle button in the corner of your site' },
            { id: 'side_tab', label: 'Side Tab', desc: 'Vertical tab pinned to the edge of the screen' },
          ].map(s => (
            <div
              key={s.id}
              onClick={() => setTheme(p => ({ ...p, widget_style: s.id }))}
              className={cn('cursor-pointer rounded-xl border p-4 transition-all',
                theme.widget_style === s.id ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-200' : 'border-slate-200 hover:border-slate-300'
              )}
              data-testid={`widget-style-${s.id}`}
            >
              <p className="font-medium text-sm text-slate-900 mb-1">{s.label}</p>
              <p className="text-xs text-slate-500">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Position */}
      <div>
        <Label className="text-slate-700 mb-2 block text-sm">{theme.widget_style === 'side_tab' ? 'Tab Side' : 'Button Position'}</Label>
        <div className="grid grid-cols-2 gap-2">
          {(theme.widget_style === 'side_tab'
            ? [{ val: 'right', label: 'Right Edge' }, { val: 'left', label: 'Left Edge' }]
            : [{ val: 'bottom-right', label: 'Bottom Right' }, { val: 'bottom-left', label: 'Bottom Left' }]
          ).map(pos => (
            <Button key={pos.val} variant={theme.position === pos.val ? 'default' : 'outline'} className={theme.position === pos.val ? 'bg-teal-600' : ''} onClick={() => setTheme(p => ({ ...p, position: pos.val }))}>
              {pos.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Chat Button Icon */}
      <div>
        <Label className="text-slate-700 mb-2 block text-sm">Chat Button Icon</Label>
        <p className="text-xs text-slate-500 mb-3">This icon appears on the chat button visitors see on your website.</p>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {[
            { id: 'chat',       label: 'Chat',     Icon: MessageSquare },
            { id: 'bot',        label: 'Bot',      Icon: Bot },
            { id: 'headphones', label: 'Support',  Icon: Headphones },
            { id: 'sparkles',   label: 'AI',       Icon: Sparkles },
            { id: 'help',       label: 'Help',     Icon: HelpCircle },
            { id: 'phone',      label: 'Phone',    Icon: Phone },
            { id: 'heart',      label: 'Friendly', Icon: Heart },
            { id: 'star',       label: 'Premium',  Icon: Star },
            { id: 'zap',        label: 'Fast',     Icon: Zap },
          ].map((opt) => {
            const active = theme.widget_button_icon === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setTheme(p => ({ ...p, widget_button_icon: opt.id }))}
                data-testid={`wizard-icon-btn-${opt.id}`}
                className={cn(
                  'flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl border-2 transition-all',
                  active
                    ? 'border-teal-500 bg-teal-50 text-teal-700 shadow-sm'
                    : 'border-slate-200 hover:border-slate-300 text-slate-600 hover:bg-slate-50'
                )}
              >
                <opt.Icon className="w-5 h-5" />
                <span className="text-xs font-medium">{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Optional Button Label */}
      <div>
        <Label className="text-slate-700 mb-2 block text-sm">Button Text Label <span className="text-slate-400 text-xs">(optional, max 20 chars)</span></Label>
        <Input
          value={theme.widget_button_text || ''}
          onChange={(e) => setTheme(p => ({ ...p, widget_button_text: e.target.value.slice(0, 20) }))}
          placeholder="e.g. Chat with us"
          className="bg-white border-slate-300"
          data-testid="wizard-button-text-input"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Keep these labels IN SYNC with ThemeTab.jsx COLOR_FIELDS — the
            wizard preview, Details page preview, and live widget all read the
            same theme keys, so the labels users see for each swatch must
            match everywhere. */}
        {[
          { key: 'primary_color', label: 'Header / Brand Color' },
          { key: 'secondary_color', label: 'Accent Color' },
          { key: 'text_color', label: 'Message Text Color' },
          { key: 'background_color', label: 'Chat Background' },
        ].map(c => (
          <div key={c.key}>
            <Label className="text-slate-700 mb-2 block text-sm">{c.label}</Label>
            <div className="flex items-center gap-2">
              <input type="color" value={theme[c.key]} onChange={e => setTheme(p => ({ ...p, [c.key]: e.target.value }))} className="w-10 h-10 rounded cursor-pointer border-0" />
              <Input value={theme[c.key]} onChange={e => setTheme(p => ({ ...p, [c.key]: e.target.value }))} className="bg-white border-slate-300 font-mono text-sm" />
            </div>
          </div>
        ))}
      </div>

      <div>
        <Label className="text-slate-700 mb-2 block text-sm">Border Radius: {theme.border_radius}px</Label>
        <Slider value={[theme.border_radius]} onValueChange={([v]) => setTheme(p => ({ ...p, border_radius: v }))} min={0} max={24} step={2} className="mt-2" />
      </div>

      {/* Live Preview */}
      <div className="bg-slate-100 rounded-xl p-6 text-center">
        <p className="text-xs text-slate-500 mb-3">Preview</p>
        <div className="inline-block rounded-2xl shadow-lg overflow-hidden border border-slate-200" style={{ borderRadius: theme.border_radius, maxWidth: 280 }}>
          <div className="p-3 text-white text-sm font-medium text-left flex items-center gap-1.5" style={{ backgroundColor: theme.primary_color }}>
            {(() => {
              const iconMap = { chat: MessageSquare, bot: Bot, headphones: Headphones, sparkles: Sparkles, help: HelpCircle, phone: Phone, heart: Heart, star: Star, zap: Zap };
              const PreviewIcon = iconMap[theme.widget_button_icon] || Bot;
              return <PreviewIcon className="w-4 h-4" />;
            })()}
            <span>{name || 'AI Employee'}</span>
          </div>
          <div className="p-4 text-left" style={{ backgroundColor: theme.background_color }}>
            <div className="inline-block px-3 py-2 rounded-lg text-sm" style={{ backgroundColor: theme.secondary_color, color: theme.text_color }}>
              {welcome || 'Hello! How can I help?'}
            </div>
          </div>
        </div>
        <div className="mt-3 flex justify-center">
          <div className={cn(
            'flex items-center gap-2 shadow-lg cursor-default',
            theme.widget_button_text ? 'rounded-full px-4 py-2 h-10' : 'w-12 h-12 rounded-full justify-center'
          )} style={{ background: theme.primary_color }}>
            {(() => {
              const iconMap = { chat: MessageSquare, bot: Bot, headphones: Headphones, sparkles: Sparkles, help: HelpCircle, phone: Phone, heart: Heart, star: Star, zap: Zap };
              const ButtonIcon = iconMap[theme.widget_button_icon] || MessageSquare;
              return <ButtonIcon className="w-5 h-5 text-white" />;
            })()}
            {theme.widget_button_text && <span className="text-white text-xs font-bold">{theme.widget_button_text}</span>}
          </div>
        </div>
      </div>
    </div>
  );

  const renderDeploy = () => {
    const connectedChannels = CHANNEL_DEFS.filter(ch => channelToggles[ch.id]).map(ch => ch.name);
    return (
      <div className="space-y-6 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <Rocket className="w-8 h-8 text-green-600" />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-slate-900">Ready to Launch!</h3>
          <p className="text-sm text-slate-500 mt-2">Your agent will go live immediately. You'll get an embed code to paste on your website, and channel credentials will be saved.</p>
        </div>

        <div className="bg-slate-50 rounded-xl p-5 text-left space-y-3 border border-slate-200">
          {[
            ['Name', name],
            ['Template', TEMPLATES.find(t => t.id === template)?.name],
            ['Knowledge', `${files.length} files, ${urls.length} URLs`],
            ['Rules', `${rules.length} rules`],
            ['Tone', tone],
            ['Widget Style', theme.widget_style === 'side_tab' ? 'Side Tab' : 'Floating Button'],
            ['Channels', connectedChannels.join(', ') || 'Website only'],
          ].map(([label, value]) => (
            <div key={label} className="flex items-center justify-between">
              <span className="text-sm text-slate-500">{label}</span>
              {label === 'Template'
                ? <Badge className="bg-teal-100 text-teal-700">{value}</Badge>
                : <span className="text-sm text-slate-700 capitalize">{value}</span>
              }
            </div>
          ))}
        </div>

        <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 text-sm text-teal-700">
          <CheckCircle className="w-4 h-4 inline mr-2" />
          Your agent will be created as <strong>Active</strong> and ready to use immediately.
        </div>
      </div>
    );
  };

  const RENDER_MAP = [renderBasics, renderKnowledge, renderPersonality, renderChannels, renderTheme, renderDeploy];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-0 [&>button.absolute]:hidden" data-testid="create-agent-wizard" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogTitle className="sr-only">Create AI Employee</DialogTitle>
        <DialogDescription className="sr-only">Multi-step wizard to set up a new AI Employee with knowledge, personality, channels, appearance, and deployment.</DialogDescription>
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-slate-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900">Create AI Employee</h2>
            <Button variant="ghost" size="icon" onClick={handleClose}><X className="w-5 h-5" /></Button>
          </div>

          {/* Steps */}
          <div className="flex items-center gap-2 mb-4">
            {STEPS.map((s, idx) => (
              <React.Fragment key={s.id}>
                <button
                  onClick={() => idx <= step && setStep(idx)}
                  className={cn("flex items-center gap-2 px-3 py-2 rounded-lg transition-all",
                  idx === step ? "bg-teal-100 text-teal-700" : idx < step ? "bg-green-100 text-green-700 cursor-pointer hover:bg-green-200" : "bg-slate-100 text-slate-400"
                  )}
                  data-testid={`wizard-step-${s.id}`}
                >
                  {idx < step ? <Check className="w-4 h-4" /> : <s.icon className="w-4 h-4" />}
                  <span className="text-sm font-medium hidden md:inline">{s.title}</span>
                </button>
                {idx < STEPS.length - 1 && <div className={cn("w-8 h-0.5", idx < step ? "bg-green-300" : "bg-slate-200")} />}
              </React.Fragment>
            ))}
          </div>
          <Progress value={progress} className="h-1" />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {RENDER_MAP[step]()}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between bg-slate-50">
          <Button variant="outline" onClick={handleBack} disabled={step === 0} className="gap-2" data-testid="wizard-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back
          </Button>
          <span className="text-sm text-slate-500">Step {step + 1} of {STEPS.length}</span>
          {step < STEPS.length - 1 ? (
            <Button onClick={handleNext} disabled={!canProceed()} className="gap-2 bg-teal-600 hover:bg-teal-700" data-testid="wizard-next-btn">
              Next <ArrowRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button onClick={handleCreate} disabled={submitting || !canProceed()} className="gap-2 bg-green-600 hover:bg-green-700" data-testid="wizard-create-btn">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</> : <><Rocket className="w-4 h-4" /> Create Agent</>}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateAgentWizard;
